import HttpStatus from "http-status-codes";
import flatten from "lodash/flatten";
import logger from "../logger";
import { getLocalizedText } from "../utils/i18n/i18n.utils";
import { capitalizeString, join, stringToPascalCaseWithSpaces, stringToSnakeCase, } from "./string.utils";
var getReadableFieldName = function (input) {
    var fieldNameSnakeCase = stringToSnakeCase(input);
    var fieldNameKey = "field.".concat(fieldNameSnakeCase);
    var fieldNameTranslated = getLocalizedText(fieldNameKey);
    // If field name not translated: return field name formatted as pascal case with spaces
    if (fieldNameTranslated === fieldNameKey) {
        var fieldNamePascalCaseWithSpaces = stringToPascalCaseWithSpaces(input);
        return fieldNamePascalCaseWithSpaces;
    }
    return fieldNameTranslated;
};
var getMessageForLocalizableValidationError = function (entry) {
    var _a, _b;
    var parsed;
    try {
        parsed = JSON.parse(entry);
    }
    catch (_c) {
        // Not every V2 message is one of ours - a class-validator default comes
        // through as a plain English sentence. Show it as it arrived.
        return entry;
    }
    if (!(parsed === null || parsed === void 0 ? void 0 : parsed.localizable) || !parsed.key) {
        return entry;
    }
    var fieldKey = (_a = parsed.values) === null || _a === void 0 ? void 0 : _a.field;
    var translatedField = fieldKey ? getLocalizedText(fieldKey) : undefined;
    return getLocalizedText(parsed.key, {
        // An untranslated field key resolves to itself; fall back to the readable
        // form of its last segment rather than showing `system.field.owner_name`.
        field: translatedField && translatedField !== fieldKey
            ? translatedField
            : getReadableFieldName((fieldKey === null || fieldKey === void 0 ? void 0 : fieldKey.split(".").pop()) || ""),
        value: ((_b = parsed.values) === null || _b === void 0 ? void 0 : _b.value) || "",
    });
};
/**
 * V2 answers a validation failure with an array of messages, one per rejected
 * field, where V1 nested them under `error.details.codes`. Both shapes are read
 * here: without the V2 branch every validation failure - a malformed IBAN being
 * the most common one a user meets - fell through to the generic message.
 */
var getErrorForUnprocessableEntity = function (axiosError) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    try {
        var messages = ((_d = (_c = (_b = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.exception) === null || _c === void 0 ? void 0 : _c.response) === null || _d === void 0 ? void 0 : _d.message) ||
            ((_f = (_e = axiosError.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.message);
        if (Array.isArray(messages)) {
            var translated = messages
                .filter(function (message) { return typeof message === "string"; })
                .map(getMessageForLocalizableValidationError)
                .filter(function (message) { return message.length > 0; });
            if (translated.length > 0) {
                return join(translated);
            }
        }
        var codes_1 = ((_k = (_j = (_h = (_g = axiosError.response) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.error) === null || _j === void 0 ? void 0 : _j.details) === null || _k === void 0 ? void 0 : _k.codes) || {};
        var results = Object.keys(codes_1).map(function (fieldName) {
            var readableFieldName = getReadableFieldName(fieldName);
            return codes_1[fieldName].map(function (fieldError) {
                var fieldErrorSakeCase = stringToSnakeCase(fieldError);
                return getLocalizedText("error.".concat(fieldErrorSakeCase), {
                    field: readableFieldName,
                });
            });
        });
        var flattenedResults = flatten(results);
        if (flattenedResults.length === 0) {
            return;
        }
        return join(flattenedResults);
    }
    catch (unknownError) {
        var error = unknownError;
        logger.logError({ error: error });
        return;
    }
};
export var isNotFoundError = function (error) {
    var _a;
    var axiosError = error;
    if (!(axiosError === null || axiosError === void 0 ? void 0 : axiosError.isAxiosError)) {
        return false;
    }
    return ((_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.status) === HttpStatus.NOT_FOUND;
};
export var getMessageForError = function (error) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    var axiosError = error;
    if (!axiosError.isAxiosError) {
        return getLocalizedText("general.error");
    }
    var result = getLocalizedText("general.error");
    if (axiosError.code === "ECONNABORTED") {
        result = getLocalizedText("system.error.timeout");
    }
    var messageFromResponse = 
    // V2
    ((_d = (_c = (_b = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.exception) === null || _c === void 0 ? void 0 : _c.response) === null || _d === void 0 ? void 0 : _d.message) ||
        ((_g = (_f = (_e = axiosError.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.exception) === null || _g === void 0 ? void 0 : _g.message) ||
        (
        // Legacy
        (_k = (_j = (_h = axiosError.response) === null || _h === void 0 ? void 0 : _h.data) === null || _j === void 0 ? void 0 : _j.error) === null || _k === void 0 ? void 0 : _k.message) ||
        ((_m = (_l = axiosError.response) === null || _l === void 0 ? void 0 : _l.data) === null || _m === void 0 ? void 0 : _m.message);
    // V2 validation failures answer with an array of messages, one per rejected
    // field. Passing that to getLocalizedText returns the array itself, which
    // then throws on the string handling below -- and a throw here means the
    // alert trigger never renders, so the user sees nothing at all.
    if (typeof messageFromResponse === "string") {
        result = getLocalizedText(messageFromResponse);
    }
    switch ((_o = axiosError.response) === null || _o === void 0 ? void 0 : _o.status) {
        case HttpStatus.UNPROCESSABLE_ENTITY:
            result = getErrorForUnprocessableEntity(axiosError) || result;
            break;
        case HttpStatus.FORBIDDEN:
            result = getLocalizedText("system.error.access_denied");
            break;
        case HttpStatus.NOT_FOUND:
            result = getLocalizedText("system.error.404");
            break;
        // TODO: Add other HTTP statuses
        default:
            break;
    }
    // Messages that already carry their own punctuation -- API-supplied message
    // keys in particular -- would otherwise render with a doubled period.
    var endsWithPunctuation = /[.!?…]$/.test(result.trim());
    return capitalizeString(endsWithPunctuation ? result : "".concat(result, "."));
};
